import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { QuestionVisibility } from '@models/api/question';
import type { UserTagSelection } from '@components/TagFriendDrawer';

export interface QuestionDraft {
  title: string;
  description: string;
  guestEmail: string;
  selectedEvents: string[];
  selectedSpaces: string[];
  visibility: QuestionVisibility;
  slug?: string;
  isAnonymous: boolean;
  askingForSelf: boolean;
  askingForSelection: UserTagSelection | null;
}

interface DraftState {
  draft: QuestionDraft | null;
  saveDraft: (draft: QuestionDraft) => void;
  clearDraft: () => void;
}

export const migrateV1Draft = (persisted: unknown): QuestionDraft | null => {
  // If persisted is not a plain object, fallback to a fresh draft so we
  // never leak an unexpected shape into the store.
  if (persisted === null || typeof persisted !== 'object') {
    return null;
  }

  const migrateStringArray = (p: unknown): string[] => {
    if (!Array.isArray(p)) return [];
    return p.filter((val): val is string => typeof val === 'string');
  };

  // Returns null on unknown visibility (rather than defaulting to a value)
  // so the caller can drop the draft rather than silently widening it. If
  // we ever rename a QuestionVisibility literal, old persisted drafts would
  // otherwise rehydrate as 'anyone' (the most permissive option), broadcasting
  // a user's draft wider than they intended. Losing an in-progress draft is
  // recoverable; leaking visibility isn't.
  const parseVisibility = (vis: unknown): QuestionVisibility | null => {
    if (typeof vis === 'string') {
      const foundVis = Object.values(QuestionVisibility).find(typedVis => vis === typedVis);
      if (foundVis) return foundVis;
    }
    return null;
  };

  const validateAskingForSelection = (sel: unknown): UserTagSelection | null => {
    if (sel === null || sel === undefined) return null;
    if (typeof sel !== 'object') return null;

    const s = sel as Record<string, unknown>;

    if (s.type === 'user') {
      if (typeof s.userId !== 'string' || !s.user || typeof s.user !== 'object') return null;
      const u = s.user as Record<string, unknown>;
      if (typeof u.id !== 'string' || typeof u.full_name !== 'string') return null;
      return sel as UserTagSelection;
    }

    if (s.type === 'email') {
      if (typeof s.email !== 'string') return null;
      return sel as UserTagSelection;
    }

    return null;
  };

  const p = persisted as Record<string, unknown>;
  const visibility = parseVisibility(p.visibility);
  if (visibility === null) return null;

  return {
    title: typeof p.title === 'string' ? p.title : '',
    description: typeof p.description === 'string' ? p.description : '',
    guestEmail: typeof p.guestEmail === 'string' ? p.guestEmail : '',
    selectedEvents: migrateStringArray(p.selectedEvents),
    selectedSpaces: migrateStringArray(p.selectedSpaces),
    visibility,
    slug: typeof p.slug === 'string' ? p.slug : undefined,
    isAnonymous: typeof p.isAnonymous === 'boolean' ? p.isAnonymous : false,
    askingForSelf: typeof p.askingForSelf === 'boolean' ? p.askingForSelf : true,
    askingForSelection: validateAskingForSelection(p.askingForSelection),
  };
};

export const useCreateQuestionDraftStore = create<DraftState>()(
  persist(
    set => ({
      draft: null,
      saveDraft: draft => {
        set({ draft });
      },
      clearDraft: () => {
        set({ draft: null });
      },
    }),
    {
      name: 'create-question-draft',
      version: 2,
      migrate: (_persisted, fromVersion) => {
        // Corrupted or first-time state: don't trust the cast. Return a
        // fresh shell; zustand re-attaches saveDraft / clearDraft.
        if (!_persisted || typeof _persisted !== 'object') {
          return { draft: null } as DraftState;
        }
        const persisted = _persisted as { draft?: unknown };
        if (fromVersion < 2) {
          return { ...persisted, draft: migrateV1Draft(persisted.draft) } as DraftState;
        }
        return _persisted as DraftState;
      },
      storage: createJSONStorage(() => globalThis.sessionStorage),
    },
  ),
);
